import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AppLayout } from "./components/AppLayout";
import { ApiClient, ApiError, type FamilyApi } from "./lib/api";
import type { SessionResponse } from "./lib/types";
import { LoginPage } from "./pages/LoginPage";
import { AllTasksPage } from "./pages/AllTasksPage";
import { TaskEditorPage } from "./pages/TaskEditorPage";
import { StatsPage } from "./pages/StatsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TodayPage } from "./pages/TodayPage";
import { RuiGardenPage } from "./pages/RuiGardenPage";

const defaultApi = new ApiClient();

type SessionState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; session: SessionResponse };

export function App() {
  return (
    <BrowserRouter basename="/app">
      <AppContent api={defaultApi} />
    </BrowserRouter>
  );
}

export function AppContent({ api }: { api: FamilyApi }) {
  const [sessionState, setSessionState] = useState<SessionState>({ status: "loading" });
  const [reloadKey, setReloadKey] = useState(0);
  const [offline, setOffline] = useState(() => !navigator.onLine);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  useEffect(() => {
    let active = true;
    setSessionState({ status: "loading" });
    api
      .getSession()
      .then((session) => {
        if (!active) return;
        api.setCsrfToken?.(session.authenticated ? session.csrfToken : null);
        setSessionState({ status: "ready", session });
      })
      .catch((error: unknown) => {
        if (!active) return;
        const message =
          error instanceof ApiError ? error.message : "目前無法確認登入狀態。";
        setSessionState({ status: "error", message });
      });
    return () => {
      active = false;
    };
  }, [api, reloadKey]);

  if (sessionState.status === "loading") return <AppLoading />;
  if (sessionState.status === "error") {
    return (
      <main className="centered-state">
        <p role="alert">{sessionState.message}</p>
        <button className="button button-secondary" onClick={() => setReloadKey((key) => key + 1)}>
          再試一次
        </button>
      </main>
    );
  }
  if (!sessionState.session.authenticated) return <LoginRoutes />;

  const signOut = async () => {
    await api.logout();
    api.setCsrfToken?.(null);
    setSessionState({ status: "ready", session: { authenticated: false } });
  };

  return (
    <AppLayout session={sessionState.session} offline={offline}>
      <AuthenticatedRoutes api={api} offline={offline} session={sessionState.session} onSignedOut={signOut} />
    </AppLayout>
  );
}

function LoginRoutes() {
  return (
    <Routes>
      <Route path="*" element={<LoginPage />} />
    </Routes>
  );
}

function AuthenticatedRoutes({ api, offline, session, onSignedOut }: {
  api: FamilyApi;
  offline: boolean;
  session: Extract<SessionResponse, { authenticated: true }>;
  onSignedOut: () => Promise<void>;
}) {
  return (
    <Routes>
      <Route path="/today" element={<TodayPage api={api} offline={offline} session={session} />} />
      <Route path="/tasks" element={<AllTasksPage api={api} offline={offline} session={session} />} />
      <Route path="/tasks/new" element={<TaskEditorPage api={api} offline={offline} session={session} />} />
      <Route path="/tasks/:templateId/edit" element={<TaskEditorPage api={api} offline={offline} session={session} />} />
      <Route path="/stats" element={<StatsPage api={api} offline={offline} />} />
      <Route path="/settings" element={<SettingsPage api={api} offline={offline} memberName={session.member.displayName} canManageDevices={session.capabilities.canManageDevices} onSignedOut={onSignedOut} />} />
      <Route path="/esp" element={session.capabilities.canManageDevices ? <RuiGardenPage api={api} offline={offline} /> : <Navigate to="/today" replace />} />
      <Route path="*" element={<Navigate to="/today" replace />} />
    </Routes>
  );
}

function AppLoading() {
  return (
    <main className="app-loading" aria-busy="true" aria-label="正在載入家庭待辦">
      <span className="loading-mark" aria-hidden="true" />
      <p>正在打開我們家的待辦…</p>
    </main>
  );
}
